"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Link from "next/link";

import { Container } from "@/app/(marketing)/_components/ui/container";
import {
  formatCompactCount,
  formatCount,
  NETWORK_COPY,
  NETWORK_STATS,
} from "@/config/network-stats";
import { useSession } from "@/hooks/use-session";

import ExpertsHeroBadge from "./experts-hero-badge";

// "Average pay $78/hr" is deliberately absent: pay is authored per listing and
// mostly per-session, so there is no hourly average to state. The per-listing
// ranges on the cards below carry the pay signal instead.
const stats = [
  {
    label: "Registered experts",
    value: formatCompactCount(NETWORK_STATS.registered),
  },
  {
    label: "Open opportunities",
    value: formatCount(NETWORK_STATS.openOpportunities),
  },
  { label: "Countries", value: formatCount(NETWORK_STATS.countries) },
];

export default function ExpertsHero() {
  const { user, session } = useSession();
  const isSignedIn = Boolean(user);

  return (
    <section className="relative px-5 pt-16 pb-28 md:px-8 md:pt-20 md:pb-32 lg:px-14">
      <Container>
        <motion.div
          initial={{ opacity: 0, transform: "translateY(20px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5 }}
          className="mx-auto flex w-full max-w-[914px] flex-col items-center gap-10 sm:gap-12"
        >
          <ExpertsHeroBadge session={session} />

          <div className="flex flex-col items-center gap-5 text-center text-white">
            <h1 className="text-heading-40 sm:text-heading-48 md:text-heading-64 max-w-[820px] text-balance">
              {isSignedIn && user?.name
                ? `Welcome back, ${String(user.name).split(" ")[0]}`
                : "Get paid for your Expertise"}
            </h1>
            <p className="text-copy-16 max-w-[680px] text-balance text-white/80">
              <span className="hidden sm:inline">
                Everyone is an expert in something.{" "}
              </span>
              Join {NETWORK_COPY.registeredExperts} working remotely on research
              projects with the most advanced AI companies.
            </p>
          </div>

          <div className="flex flex-col items-center gap-3">
            <Link
              href={isSignedIn ? "/me/opportunities" : "/login"}
              className="text-marketing-blue group text-button-16 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-white px-8 transition-colors hover:bg-white/90"
            >
              {isSignedIn ? "View Opportunities" : "Claim your profile"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              href="/researchers"
              className="group text-copy-14 inline-flex items-center gap-1.5 text-white/70 transition-colors hover:text-white"
            >
              Hiring experts? See researcher tools
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>

          <div className="scrollbar-hide flex w-full justify-center overflow-x-auto">
            <div className="rounded-full border border-white/15 bg-white/10 px-1 backdrop-blur-sm">
              <ul className="text-label-12 lg:text-label-14 flex text-white">
                {stats.map((stat, index) => (
                  <li
                    key={stat.label}
                    className={
                      "px-3 py-2.5 sm:px-4 " +
                      (index < stats.length - 1
                        ? "border-r border-white/15"
                        : "")
                    }
                  >
                    <span className="text-white/80">{stat.label}</span>
                    <span className="pl-2 font-semibold tabular-nums sm:pl-6">
                      {stat.value}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </Container>
    </section>
  );
}
